<script setup lang="ts">
import {
  captcha as fetchCaptcha,
  Code,
  hasCode,
  isYcError,
  login,
  rateLimited,
} from '@yc/api-client'
import type { CaptchaResp, LoginInput } from '@yc/api-client'
import { SplitLogin } from '@yc/ui-kit'
import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()

const captcha = ref<CaptchaResp | null>(null)
const submitting = ref(false)
const error = ref<unknown>(null)
const plainError = ref('')

onMounted(async () => {
  await reloadCaptcha()
})

async function reloadCaptcha(): Promise<void> {
  try {
    captcha.value = await fetchCaptcha()
  } catch {
    captcha.value = null
  }
}

function describe(e: unknown): string {
  if (!isYcError(e)) return ''
  switch (e.code) {
    case Code.BadCredentials:
      return '企业编码、用户名或密码错误'
    case Code.CaptchaRequired:
      return '图形验证码错误或已失效，请重新输入'
    case Code.LoginLocked:
      return e.message
    case Code.UserDisabled:
      return '账号已停用，请联系管理员'
    case Code.TenantDisabled:
      return '所在企业已停用或服务已到期，请联系平台运营方'
    default:
      return e.display
  }
}

function redirectOf(): string {
  const q = route.query.redirect
  return typeof q === 'string' && q.startsWith('/') && !q.startsWith('//') ? q : '/'
}

async function onSubmit(payload: LoginInput): Promise<void> {
  submitting.value = true
  error.value = null
  plainError.value = ''
  try {
    await login(payload)
    await router.replace(redirectOf())
  } catch (e) {
    error.value = e
    plainError.value = describe(e)
    if (hasCode(e, Code.CaptchaRequired) || hasCode(e, Code.BadCredentials)) {
      await reloadCaptcha()
    }
    if (rateLimited(e)) captcha.value = null
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <SplitLogin
    app-name="云仓供应链平台"
    tagline="使用平台统一账号，登录后各系统通用"
    :captcha="captcha"
    :submitting="submitting"
    :error="error"
    :plain-error="plainError"
    @submit="onSubmit"
  />
</template>
